import React, {createContext, useState, useEffect, useRef} from "react";
import axios from "axios";
import Cookies from "js-cookie";
import {io} from "socket.io-client";
import {API_URL, SOCKETS_URL} from "../index";
import {useNavigate} from "react-router-dom"; // Import Socket.IO client

// Create Auth Context
export const AuthContext = createContext();

const AuthProvider = ({children}) => {
    const BASE_URL = 'https://api.coudy.ai/';
    const SOCKET_URL = SOCKETS_URL; // Adjust your socket server URL accordingly

    const [user, setUser] = useState(null);
    const [token, setToken] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sessions, setSessions] = useState([]);
    const [blogs, setBlogs] = useState([]);
    const [activeChat, setActiveChat] = useState(null);

    // Function to find a blog by a given key and value
    const findBlogBy = (key, value) => {
        return blogs.find((blog) => blog[key] === value);
    };

    const [isDarkMode, setIsDarkMode] = useState(false);

    const [credits, setCredits] = useState(0);

    // Create a ref for the socket so that it persists across renders.
    const socketRef = useRef(null);

    const navigate = useNavigate();

    // 🌙 **Dark Mode Handling**
    useEffect(() => {
        //check if url has ?aff=1 parameter
        console.log(window.location.search)
        if (window.location.search.includes("?aff=")) {
            const affId = window.location.search.split("?aff=")[1];
            if (affId !== null && /^\d+$/.test(affId)) {
                Cookies.set("affiliate", affId);
                axios.post(`${BASE_URL}/user/affiliate/event`, {
                    event: "clicks",
                    user_id: affId
                });
            } else {
                //fetch affiliate id from api /user/affiliate
                axios.get(`${BASE_URL}/user/affiliate/id/` + affId).then(
                    (res) => {
                        const converted = res.data.affiliate_id;
                        if (converted !== null) {
                            console.log("username conversion success")
                            Cookies.set("affiliate", converted);
                        }
                        console.log("test:" + converted)
                        axios.post(`${BASE_URL}/user/affiliate/event`, {
                            event: "clicks",
                            user_id: converted
                        });
                    }
                )
            }

            navigate(window.location.pathname);
        }

        if (!Cookies.get("darkMode")) {
            Cookies.set("darkMode", true);
        }

        const darkMode = Cookies.get("darkMode") === "true";
        setIsDarkMode(darkMode);
        document.body.classList.toggle("dark", darkMode);
        axios.get(`${BASE_URL}/user/blogs`).then(
            (res) => {
                const blogsData = res.data.blogs;
                setBlogs(blogsData);
            }
        )
    }, []);


    const toggleTheme = () => {
        setIsDarkMode((prev) => {
            const newMode = !prev;
            Cookies.set("darkMode", newMode, {expires: 30});
            document.body.classList.toggle("dark", newMode);
            return newMode;
        });
    };

    // 📌 **Fetch User Data + Stripe + Blogs**
    const loadUserData = async (authToken) => {
        try {
            const headers = {Authorization: `Bearer ${authToken}`};

            const [userRes, subRes] = await Promise.all([
                axios.get(`${BASE_URL}/user/me`, {headers}),
                axios.get(`${BASE_URL}/stripe/details`, {headers}),
            ]);

            const userData = userRes.data.user;
            const subData = subRes.data;


            const nextPaymentDate = new Date(subData.next_payment_date * 1000);
            const daysUntilNextPayment = Math.max(
                Math.ceil((nextPaymentDate - new Date()) / (1000 * 60 * 60 * 24)),
                0
            );

            setUser({
                id: userData.id,
                username: userData.username,
                email: userData.email,
                display_name: userData.display_name,
                avatar_url: userData.avatar_url,
                banner: userData.banner,
                bio: userData.bio,
                phone: userData.phone,
                name: userData.name,
                last_name: userData.last_name,
                next_payment_date: nextPaymentDate.toLocaleDateString("cs-CZ"),
                days_until_next_payment: daysUntilNextPayment,
                tier: userData.tier,
                active: subData.status,
                credits: userData.credits,
                created_at: userData.created_at,
                updated_at: userData.updated_at,
                cardHolder: userData.card_holder,
                cardNumber: userData.card_number,
                hasIsic: !!userData.verify_id,
                spotify_display_name: userData.spotify_display_name,
                specialOffer: userData.special_offer,
            });

            setCredits(userData.credits);
            setToken(authToken);

        } catch (error) {
            console.error("Error loading user data:", error);
            if (error.response?.status === 401) {
                logout();
            }
        }
    };


    // 📌 **Load User on Mount**
    useEffect(() => {
        const initAuth = async () => {
            const storedToken = localStorage.getItem("authToken");
            const urlParams = new URLSearchParams(window.location.search);

            if (storedToken) await loadUserData(storedToken);

            setLoading(false);
        };

        initAuth();
    }, []);

    // 📌 **Login Function with 2FA Support**
    const login = async (username, password) => {
        let response;
        await axios
            .post(`${BASE_URL}/user/login`, {username, password})
            .then(async (res) => {
                if (res.data.requires2FA) {
                    response = {success: true, requires2FA: true, userId: res.data.userId};
                    return; // Do not proceed until 2FA is verified
                }

                localStorage.setItem("authToken", res.data.token);
                await loadUserData(res.data.token);
                response = {success: true};
            })
            .catch((error) => {
                response = {success: false, status: error.response?.status};
            });
        return response;
    };


    const loginWithGoogle = async (credential, specialOffer = false) => {
        try {
                 const res = await axios.post(`${BASE_URL}/auth/google-login`, { credential, specialOffer });
             if (res.data.token) {
                       localStorage.setItem("authToken", res.data.token);
                     await loadUserData(res.data.token);
                        return { success: true };
                } else {
                    return { success: false, error: "Google login selhalo" };
               }
         } catch (e) {
              console.error("Google login error:", e.response?.data || e.message);
              return { success: false, error: e.response?.data?.error || "Chyba při Google loginu" };
              }
    };

    // 📌 **Verify 2FA**
    const verify2FA = async (userId, otpCode) => {
        try {
            const res = await axios.post(`${BASE_URL}/user/2fa/verify-login`, {
                userId,
                token: otpCode,
            });

            if (res.data.token) {
                localStorage.setItem("authToken", res.data.token);
                await loadUserData(res.data.token);
                return {success: true};
            }
            return {success: false};
        } catch (error) {
            console.error("2FA verification error:", error);
            throw new Error("Invalid 2FA code");
        }
    };

    // 📌 **Logout Function**
    const logout = async () => {
        try {
            await axios.delete(`${BASE_URL}/user/sessions/all`, {
                headers: {Authorization: `Bearer ${token}`},
            });
        } catch (error) {
            console.error("Logout error:", error);
        }
        localStorage.removeItem("authToken");
        setUser(null);
        setToken(null);
        setSessions([]);

        // Disconnect socket if connected
        if (socketRef.current) {
            socketRef.current.disconnect();
            socketRef.current = null;
        }
    };

    // 📌 **Fetch Sessions**
    const fetchSessions = async () => {
        if (!token) return;
        try {
            const res = await axios.get(`${BASE_URL}/user/sessions`, {
                headers: {Authorization: `Bearer ${token}`},
            });
            setSessions(res.data.sessions);
        } catch (error) {
            console.error("Error fetching sessions:", error);
        }
    };

    // 📌 **Register User**
    const register = async (userData) => {
        try {
            const res = await axios.post(`${BASE_URL}/user/register`, userData);

            if (res.status === 429) {
                return {success: false, error: "Příliš mnoho registrací z jedné IP adresy, počkejte pár minut."};
            } else if (res.status !== 200 && res.status !== 201) {
                return {success: false, error: res.data.error ? res.data.error : "Registrace selhala"};
            }

            localStorage.setItem("authToken", res.data.token);
            await loadUserData(res.data.token);
            return {success: true};
        } catch (error) {
            console.error("Registration error:", error);
            if (error.response.data === "Too many registration attempts from this IP, please try again after 15 minutes") {
                return {success: false, error: "Příliš mnoho registrací z jedné IP adresy, počkejte pár minut."};
            }

            return {
                success: false,
                error: error.response.data.error ? error.response.data.error : "Registrace selhala"
            };
        }
    };

    // 📌 **Persist Socket Connection**
    useEffect(() => {
        if (!socketRef.current) {
            socketRef.current = io(SOCKET_URL, {
                transports: ["websocket"],
                reconnection: true,
            });

            socketRef.current.on("connect", () => {
                console.log("WebSocket connected:", socketRef.current.id);

                // Emit login only if token exists
                if (token) {
                    socketRef.current.emit("login", {token});
                }
            });

            socketRef.current.on("disconnect", (reason) => {
                console.log("WebSocket disconnected:", reason);
            });
        } else if (socketRef.current.connected && token) {
            // Emit login if the token updates
            socketRef.current.emit("login", {token});
        }

        // Handle token usage updates only if logged in
        const handleTokenUsageUpdate = (data) => {
            setCredits((prev) => prev - data.tokenUsage);
        };

        if (token) {
            socketRef.current.on("token_usage_update", handleTokenUsageUpdate);
        }

        // Cleanup on token change or unmount
        return () => {
            socketRef.current?.off("token_usage_update", handleTokenUsageUpdate);
        };
    }, [token, SOCKET_URL]);

    const [threadsSidebarOpen, setThreadsSidebarOpen] = useState(false);


    // 📌 **Context Value**
    const authContextValue = {
        user,
        token,
        loading,
        sessions,
        login,
        verify2FA,
        logout,
        fetchSessions,
        register,
        toggleTheme,
        credits,
        isDarkMode,
        findBlogBy,
        blogs,
        setActiveChat,
        activeChat,
        loginWithGoogle,
        socket: socketRef.current, // You can expose the socket if needed
        threadsSidebarOpen,
        setThreadsSidebarOpen,
    };

    return <AuthContext.Provider value={authContextValue}>{!loading && children}</AuthContext.Provider>;
};

export default AuthProvider;
